import { Mic, ArrowUp } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";
import { LiveWaveform } from "@/components/ui/live-waveform";
import { useRef, useEffect, useState } from "react";
import { logger } from '@/lib/logger';

export type VoiceRecordingStatus = "idle" | "recording" | "processing" | "speaking";

interface VoiceRecordingInputProps {
  status: VoiceRecordingStatus;
  onStartRecording: () => void;
  onStopAndSend: () => void;
  recordingDuration: number; // in milliseconds
  disabled?: boolean;
  /** External MediaStream to pass to LiveWaveform (avoids duplicate getUserMedia on mobile) */
  mediaStream?: MediaStream | null;
  /** Audio element for TTS playback - used for visualizing AI speech */
  audioElement?: HTMLAudioElement | null;
}

export function VoiceRecordingInput({
  status,
  onStartRecording,
  onStopAndSend,
  recordingDuration,
  disabled = false,
  mediaStream,
  audioElement,
}: VoiceRecordingInputProps) {
  const isRecording = status === "recording";
  const isSpeaking = status === "speaking";
  const isProcessing = status === "processing";
  const showMic = status === "idle";

  // Audio visualization state for speaking
  const [audioLevels, setAudioLevels] = useState<number[]>(Array(12).fill(0.15));
  const animationFrameRef = useRef<number | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Set up audio visualization when speaking
  useEffect(() => {
    if (!isSpeaking) {
      // Clean up
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      setAudioLevels(Array(12).fill(0.15));
      return;
    }

    // Fallback animation when no audio element or visualization fails
    const startFallbackAnimation = () => {
      const animateFallback = () => {
        const bars = Array.from({ length: 12 }, (_, i) => {
          return 0.2 + Math.sin(Date.now() / 200 + i * 0.5) * 0.3;
        });
        setAudioLevels(bars);
        animationFrameRef.current = requestAnimationFrame(animateFallback);
      };
      animateFallback();
    };

    if (!audioElement) {
      startFallbackAnimation();
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }

    const setupAnalyser = async () => {
      try {
        // Create audio context if needed
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new AudioContext();
        }

        const audioContext = audioContextRef.current;
        
        // Resume if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Create analyser if not exists
        if (!analyserRef.current) {
          analyserRef.current = audioContext.createAnalyser();
          analyserRef.current.fftSize = 64;
          analyserRef.current.smoothingTimeConstant = 0.5;
        }
        
        const analyser = analyserRef.current;

        // Only create source node once per audio element
        // MediaElementAudioSourceNode can only be created once per element
        if (!sourceNodeRef.current) {
          try {
            const source = audioContext.createMediaElementSource(audioElement);
            source.connect(analyser);
            analyser.connect(audioContext.destination);
            sourceNodeRef.current = source;
          } catch (e) {
            // Element may already have a source attached, use fallback
            logger.warn("VoiceRecordingInput", "Could not create source node:", e);
            startFallbackAnimation();
            return;
          }
        }

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevels = () => {
          if (!analyserRef.current) return;

          analyserRef.current.getByteFrequencyData(dataArray);

          // Convert to 12 bars with normalized levels
          const bars = Array.from({ length: 12 }, (_, i) => {
            const idx = Math.floor((i / 12) * dataArray.length);
            return Math.max(0.15, dataArray[idx] / 255);
          });

          setAudioLevels(bars);
          animationFrameRef.current = requestAnimationFrame(updateLevels);
        };

        updateLevels();
      } catch (error) {
        logger.error("VoiceRecordingInput", "Error setting up audio analyser:", error);
        startFallbackAnimation();
      }
    };

    setupAnalyser();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isSpeaking, audioElement]);

  // Format duration as mm:ss
  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleClick = () => {
    // NOTE: Do NOT call unlockAudio() here - it interferes with getUserMedia() on iOS
    // Audio unlock is handled separately via the "Activer l'audio" button
    if (isRecording) {
      onStopAndSend();
    } else if (showMic) {
      onStartRecording();
    }
  };

  return (
    <div className="flex items-center justify-center">
      <AnimatePresence mode="wait">
        {/* Idle state - Circular mic button */}
        {showMic && (
          <motion.button
            key="idle"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClick}
            disabled={disabled}
            className={cn(
              "h-16 w-16 rounded-full bg-muted hover:bg-muted/80",
              "flex items-center justify-center",
              "transition-colors duration-200",
              "border border-border",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <Mic className="h-6 w-6 text-muted-foreground" />
          </motion.button>
        )}

        {/* Processing state - Black circle with animated waiting waves */}
        {isProcessing && (
          <motion.div
            key="processing"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "h-16 w-16 rounded-full bg-foreground",
              "flex items-center justify-center gap-0.5"
            )}
          >
            {/* Animated waiting waves */}
            {Array.from({ length: 8 }).map((_, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-full bg-background"
                animate={{
                  height: [4, 14, 4],
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: [0.4, 0, 0.2, 1],
                }}
              />
            ))}
          </motion.div>
        )}

        {/* Recording state - Wide pill with scrolling waveform */}
        {isRecording && (
          <motion.div
            key="recording"
            initial={{ width: 64, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 64, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              "h-14 rounded-full bg-destructive/10 border border-destructive/30",
              "flex items-center gap-md px-lg overflow-hidden"
            )}
          >
            {/* Recording indicator + timer */}
            <div className="flex items-center gap-sm min-w-[56px] shrink-0">
              <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
              <span className="text-body-md text-destructive tabular-nums">
                {formatDuration(recordingDuration)}
              </span>
            </div>

            {/* LiveWaveform in scrolling mode - use shared stream */}
            <div className="flex-1 h-8 overflow-hidden">
              <LiveWaveform
                mode="scrolling"
                active={true}
                historySize={40}
                barColor="hsl(var(--destructive))"
                fadeEdges={true}
                externalStream={mediaStream}
              />
            </div>

            {/* Send button (arrow up - like messaging apps) */}
            <button
              onClick={onStopAndSend}
              className={cn(
                "h-10 w-10 rounded-full bg-primary shrink-0",
                "flex items-center justify-center",
                "hover:bg-primary/90 transition-colors border border-primary/50"
              )}
            >
              <ArrowUp className="h-5 w-5 text-primary-foreground" />
            </button>
          </motion.div>
        )}

        {/* Speaking state - Black circle with waveform visualization */}
        {isSpeaking && (
          <motion.div
            key="speaking"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "h-16 w-16 rounded-full bg-foreground",
              "flex items-center justify-center gap-0.5"
            )}
          >
            {/* Waveform bars */}
            {audioLevels.map((height, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-full bg-background"
                animate={{
                  height: `${Math.max(4, height * 24)}px`,
                }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
